import { useState, useEffect, useCallback } from "react"
import { useParams, useNavigate } from "react-router"
import { getBrowserSupabaseClient } from "@/lib/supabase/client"
import { useAuthStore } from "@/shared/hooks/use-auth-store"
import { LoadingSkeleton } from "@/client/components/common/loading-skeleton"
import { useToast } from "@/client/components/common/toast"
import type { User } from "@/lib/supabase/types"
import styles from "./user-profile-page.module.css"

const BackIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M19 12H5M12 19l-7-7 7-7"/>
  </svg>
)

const UserIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
)

const SchoolIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
    <path d="M6 12v5c3 3 9 3 12 0v-5"/>
  </svg>
)

const BriefcaseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
    <path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/>
  </svg>
)

const TagIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/>
    <line x1="7" y1="7" x2="7.01" y2="7"/>
  </svg>
)

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
)

const StarIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
  </svg>
)

const GithubIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 00-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0020 4.77 5.07 5.07 0 0019.91 1S18.73.65 16 2.48a13.38 13.38 0 00-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 005 4.77a5.44 5.44 0 00-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 009 18.13V22"/>
  </svg>
)

const LevelIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
)

const EmptyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="12" cy="12" r="10"/>
    <path d="M16 16s-1.5-2-4-2-4 2-4 2"/>
    <line x1="9" y1="9" x2="9.01" y2="9"/>
    <line x1="15" y1="9" x2="15.01" y2="9"/>
  </svg>
)

function UserLoading() {
  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.backBtn}><BackIcon /></div>
        <span className={styles.headerTitle}>用户资料</span>
        <div style={{ width: 64 }} />
      </div>
      <div className={styles.hero}>
        <div className={styles.heroBg} />
        <div className={styles.heroContent}>
          <LoadingSkeleton variant="circular" width={96} height={96} style={{ margin: "0 auto 16px" }} />
          <LoadingSkeleton variant="text" width="40%" height={24} style={{ margin: "0 auto 8px" }} />
          <LoadingSkeleton variant="text" width="30%" height={14} style={{ margin: "0 auto 16px" }} />
          <LoadingSkeleton variant="text" width="60%" height={48} style={{ margin: "0 auto" }} />
        </div>
      </div>
      <div style={{ padding: "0 16px" }}>
        <LoadingSkeleton variant="text" width="100%" height={60} />
        <LoadingSkeleton variant="text" width="100%" height={60} style={{ marginTop: 8 }} />
      </div>
    </div>
  )
}

export default function UserProfilePage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user: currentUser } = useAuthStore()
  const toast = useToast()

  const [profile, setProfile] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const isSelf = currentUser?.id === id

  const fetchProfile = useCallback(async () => {
    if (!id) return
    const supabase = getBrowserSupabaseClient()
    if (!supabase) { setError("数据库未配置"); setLoading(false); return }

    setLoading(true)
    setError(null)

    try {
      const { data, error: fetchErr } = await supabase
        .from("users")
        .select("*")
        .eq("id", id)
        .single()

      if (fetchErr || !data) { setError("用户不存在"); setLoading(false); return }
      setProfile(data as User)
    } catch {
      setError("加载失败，请重试")
      toast.error("加载用户资料失败")
    } finally {
      setLoading(false)
    }
  }, [id, toast])

  useEffect(() => { fetchProfile() }, [fetchProfile])

  if (loading) return <UserLoading />

  if (error || !profile) {
    return (
      <div className={styles.page}>
        <div className={styles.header}>
          <button className={styles.backBtn} onClick={() => navigate(-1)}><BackIcon /></button>
          <span className={styles.headerTitle}>用户资料</span>
          <div style={{ width: 64 }} />
        </div>
        <div style={{ padding: "60px 16px", textAlign: "center" }}>
          <p style={{ fontSize: 16, color: "var(--t2)", marginBottom: 16 }}>{error || "用户不存在"}</p>
          <button onClick={fetchProfile}
            style={{ padding: "10px 24px", borderRadius: 10, border: "1px solid var(--bd)", background: "var(--bg3)", color: "var(--t)", cursor: "pointer", fontSize: 14 }}>
            重新加载
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>
          <BackIcon />
        </button>
        <div className={styles.headerTitle}>{isSelf ? "个人中心" : "用户资料"}</div>
        <div style={{ width: 64 }} />
      </div>

      {/* Hero Section */}
      <div className={styles.hero}>
        <div className={styles.heroBg} />
        <div className={styles.heroContent}>
          <div className={styles.avatarWrapper}>
            {profile.avatar_url ? (
              <img src={profile.avatar_url} alt={profile.nickname} className={styles.avatar} />
            ) : (
              <div className={styles.avatarPlaceholder}>
                <UserIcon />
              </div>
            )}
          </div>
          <h1 className={styles.userName}>{profile.nickname}</h1>
          {profile.school && <p className={styles.userSchool}>{profile.school}</p>}
          {profile.is_verified && (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 12px", background: "var(--gd)", borderRadius: 20, fontSize: 12, color: "white", fontWeight: 600, marginBottom: 12 }}>
              <ShieldIcon /> 已认证
            </div>
          )}
          <div className={styles.statsRow}>
            <div className={styles.statItem}>
              <span className={styles.statValue}>{profile.level}</span>
              <span className={styles.statLabel}>等级</span>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.statItem}>
              <span className={styles.statValue}>{profile.points}</span>
              <span className={styles.statLabel}>积分</span>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.statItem}>
              <span className={styles.statValue}>{profile.role ? profile.role.split(",").filter(Boolean).length : 0}</span>
              <span className={styles.statLabel}>身份</span>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Info */}
      <div style={{ padding: "0 16px 16px" }}>
        <div className={styles.infoCard}>
          {profile.bio && (
            <div className={styles.infoItem}>
              <div className={styles.infoIcon}><StarIcon /></div>
              <div className={styles.infoContent}>
                <span className={styles.infoLabel}>个人简介</span>
                <span className={styles.infoValue}>{profile.bio}</span>
              </div>
            </div>
          )}

          {profile.company && (
            <div className={styles.infoItem}>
              <div className={styles.infoIcon}><BriefcaseIcon /></div>
              <div className={styles.infoContent}>
                <span className={styles.infoLabel}>公司/组织</span>
                <span className={styles.infoValue}>{profile.company}</span>
              </div>
            </div>
          )}

          {profile.direction && (
            <div className={styles.infoItem}>
              <div className={styles.infoIcon}><LevelIcon /></div>
              <div className={styles.infoContent}>
                <span className={styles.infoLabel}>发展方向</span>
                <span className={styles.infoValue}>{profile.direction}</span>
              </div>
            </div>
          )}

          {profile.role && profile.role.split(",").filter(Boolean).length > 0 && (
            <div className={`${styles.infoItem}`}>
              <div className={styles.infoIcon}><TagIcon /></div>
              <div className={styles.infoContent}>
                <span className={styles.infoLabel}>身份标签</span>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                  {profile.role.split(",").filter(Boolean).map((tag) => (
                    <span key={tag} style={{
                      padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 500,
                      background: "var(--cyd)", color: "var(--cy)",
                    }}>
                      {tag === "engineer" ? "工程师" : tag === "enterprise" ? "企业" : tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {profile.github_url && (
            <div className={`${styles.infoItem} ${styles.infoItemLast}`}>
              <div className={styles.infoIcon}><GithubIcon /></div>
              <div className={styles.infoContent}>
                <span className={styles.infoLabel}>GitHub</span>
                <a
                  href={profile.github_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: 14, color: "var(--bl)", textDecoration: "none" }}
                >
                  {profile.github_url.replace("https://github.com/", "")}
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Joined Date */}
        <div style={{ textAlign: "center", padding: "16px 0", fontSize: 12, color: "var(--t3)" }}>
          加入于 {new Date(profile.created_at).toLocaleDateString("zh-CN")}
        </div>
      </div>
    </div>
  )
}
